import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  INPUT_VALIDATION_NO_WHITE_SPACE_WITH_SPECIAL_CHARACTER_REGEX,
  NO_DATA,
} from "utils/constants/constant";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import {
  PLAUserListPayload,
  PLAUserListSuccessPayload,
} from "store/slpDashboard/type";
import {
  HopesGoalsAndAmbitionsPayload,
  HopesGoalsAndAmbitionsSuccessPayload,
} from "store/slpForm/types";
import { SLPFormStep } from "utils/enums/slp-form";
import {
  hideLoaderForBatchApiCall,
  showLoaderForBatchApiCall,
} from "utils/helper";
import {
  getSlpConductedByError,
  slpAnswerWhiteSpaceError,
} from "utils/helper/validation-message-helper";
import { IPLAUserOption } from "utils/interfaces/slp";
import {
  IHopesGoalsAndAmbitions,
  IHopesGoalsAndAmbitionsFormValues,
  IUpdateHopesGoalsAndAmbitions,
} from "utils/interfaces/slp-form";
import SLPFormButtons from "../SLPFormButtons";

interface IHopesGoalsAndAmbitionsProps {
  hopesGoalsAndAmbitionsRequest: (payload: HopesGoalsAndAmbitionsPayload) => {};
  plaUserListRequest: (payload: PLAUserListPayload) => {};
  handleNext: (data?: IUpdateHopesGoalsAndAmbitions) => void;
}

const HopesGoalsAndAmbitions: React.FC<IHopesGoalsAndAmbitionsProps> = ({
  hopesGoalsAndAmbitionsRequest,
  plaUserListRequest,
  handleNext,
}) => {
  const location = useLocation<{
    slpId: number;
    partNumber: SLPFormStep;
    userId: number;
  }>();
  const { slpId, partNumber, userId } = location.state;
  const [hopesGoalsAndAmbitionsData, setHopesGoalsAndAmbitionsData] =
    useState<IHopesGoalsAndAmbitions>();
  const [plaUsers, setPlaUsers] = useState<IPLAUserOption[]>([]);

  const {
    control,
    setValue,
    trigger,
    handleSubmit,
    formState: { errors },
  } = useForm<IHopesGoalsAndAmbitionsFormValues>({
    defaultValues: {
      conductedBy: "",
      answers: {},
    },
  });

  const onHopesGoalsAndAmbitionsDataSuccess = useCallback(
    (response: HopesGoalsAndAmbitionsSuccessPayload) => {
      const data = response.hopesGoalsAndAmbitionsData;
      setHopesGoalsAndAmbitionsData(data);

      setValue("conductedBy", data.allocatedTo ?? userId);

      const initialAnswers = Object.fromEntries(
        data.questionsAnswers.map((qa) => [qa.id, qa.answer || ""])
      );

      setValue("answers", initialAnswers);
      hideLoaderForBatchApiCall();
    },
    [setValue, userId]
  );

  const getHopesGoalsAndAmbitionsData = useCallback(() => {
    if (!hopesGoalsAndAmbitionsRequest) return;

    showLoaderForBatchApiCall();
    hopesGoalsAndAmbitionsRequest({
      values: {
        slpId,
        partNumber,
      },
      callback: onHopesGoalsAndAmbitionsDataSuccess,
    });
  }, [
    hopesGoalsAndAmbitionsRequest,
    onHopesGoalsAndAmbitionsDataSuccess,
    slpId,
    partNumber,
  ]);

  const onPlaUserListSuccess = (response: PLAUserListSuccessPayload) => {
    setPlaUsers(response.userList);
    hideLoaderForBatchApiCall();
  };

  const getPlaUserList = useCallback(() => {
    if (!plaUserListRequest) return;

    showLoaderForBatchApiCall();

    const payload = {
      callback: onPlaUserListSuccess,
    };
    plaUserListRequest(payload);
  }, [plaUserListRequest]);

  useEffect(() => {
    getHopesGoalsAndAmbitionsData();
  }, [getHopesGoalsAndAmbitionsData]);

  useEffect(() => {
    getPlaUserList();
  }, [getPlaUserList]);

  const onClickNext = (data: IHopesGoalsAndAmbitionsFormValues) => {
    const payload: IUpdateHopesGoalsAndAmbitions = {
      slpId,
      partNumber,
      allocatedTo: Number(data.conductedBy),
      questionAnswers: Object.entries(data.answers).map(([id, answer]) => ({
        questionId: Number(id),
        answer: answer.trim(),
      })),
    };

    handleNext(payload);
  };

  return (
    <form onSubmit={handleSubmit(onClickNext)}>
      <Typography variant="body2" className="gray-text-sm">
        Student Name
      </Typography>
      <Typography variant="h4" className="medium-text" sx={{ pb: 3 }}>
        {hopesGoalsAndAmbitionsData?.studentName}
      </Typography>
      <Typography variant="body2" className="gray-text-sm">
        Supports Required/Specific Learning Needs
      </Typography>
      <Typography
        variant="h4"
        className="medium-text"
        sx={{ pb: 3, wordWrap: "break-word" }}
      >
        {hopesGoalsAndAmbitionsData?.information || NO_DATA}
      </Typography>
      <Typography variant="h4" className="medium-text">
        Preliminary: This SLP&apos;s details
        <em style={{ color: "#EA2222" }}>*</em>
      </Typography>
      <Typography variant="overline" className="text-gray font-italic">
        In general, these details do not need to be changed.
      </Typography>

      <Grid
        container
        columnSpacing={{ xs: "16px", lg: "20px", xl: "24px" }}
        mt={1}
      >
        <Grid item xs={12} sm={6} lg={4}>
          <Controller
            name="conductedBy"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <FormControl
                fullWidth
                className="select"
                error={!!errors.conductedBy}
              >
                <InputLabel id="course1-label">
                  SLP Conducted By <span className="color-red">*</span>
                </InputLabel>
                <Select
                  labelId="course1-label"
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(Number(e.target.value));
                    trigger("conductedBy");
                  }}
                >
                  {plaUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                  {!plaUsers.length && (
                    <MenuItem disabled>No users available</MenuItem>
                  )}
                </Select>
                <FormHelperText>
                  {getSlpConductedByError(errors.conductedBy)}
                </FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <TextField
            label="SLP Number"
            fullWidth
            variant="outlined"
            value={hopesGoalsAndAmbitionsData?.slpNumber}
            disabled
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      <Typography
        variant="h4"
        className="medium-text"
        sx={{ pt: 2.5, pb: 1.5 }}
      >
        Part 1: Hopes, Goals and Ambitions
      </Typography>

      <Grid container spacing={2}>
        {hopesGoalsAndAmbitionsData?.questionsAnswers.map((qa) => (
          <Grid item xs={12} key={qa.questionOrder}>
            <Typography variant="body2" className="gray-text-sm" sx={{ pb: 1 }}>
              {`${qa.questionOrder}) ${qa.question}`}
            </Typography>
            <Controller
              name={`answers.${qa.id}`}
              key={`answers-${qa.id}`}
              control={control}
              rules={{
                pattern:
                  INPUT_VALIDATION_NO_WHITE_SPACE_WITH_SPECIAL_CHARACTER_REGEX,
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  multiline
                  fullWidth
                  rows={3}
                  className="suggestion-textarea"
                  error={!!fieldState.error}
                  helperText={slpAnswerWhiteSpaceError(fieldState.error)}
                />
              )}
            />
          </Grid>
        ))}
      </Grid>
      <SLPFormButtons activePartNumber={partNumber} />
    </form>
  );
};
export default HopesGoalsAndAmbitions;
